<template>
    <v-dialog :value="value" max-width="1100" persistent scrollable @input="cerrar">
        <v-card>
            <v-card-title class="text-subtitle-1 font-weight-bold">
                <v-icon left color="primary">palette</v-icon>
                {{ modelo.tema_id ? 'Editar tema' : 'Nuevo tema personalizado' }}
                <v-chip v-if="tema && tema.predeterminado" x-small label class="ml-3" color="primary" outlined>Predeterminado</v-chip>
                <v-spacer></v-spacer>
                <v-btn icon small @click="cerrar(false)"><v-icon>close</v-icon></v-btn>
            </v-card-title>

            <v-card-text>
                <v-row>
                    <v-col cols="12" md="7">
                        <v-form ref="form" v-model="valido" @submit.prevent="guardar">
                            <v-row dense>
                                <v-col cols="12" md="7">
                                    <v-text-field v-model="modelo.nombre" label="Nombre del tema" outlined dense maxlength="120" counter="120"
                                                  :rules="[v => !!(v && v.trim()) || 'Escriba el nombre del tema']"/>
                                </v-col>
                                <v-col cols="12" md="5">
                                    <v-switch v-model="modelo.activo" inset hide-details color="success" class="mt-1"
                                              :label="modelo.activo ? 'Activo: se puede usar' : 'Inactivo: no se usa'"/>
                                </v-col>
                                <v-col cols="12">
                                    <v-textarea v-model="modelo.descripcion" label="Descripción" outlined dense rows="2" auto-grow maxlength="500" counter="500"
                                                hint="Opcional. Para qué se usa o cuándo conviene activarlo." persistent-hint/>
                                </v-col>
                            </v-row>

                            <div class="text-subtitle-2 font-weight-bold mt-4 mb-1">Colores</div>
                            <div class="text-caption grey--text text--darken-1 mb-2">
                                El primario pinta la cabecera y los botones principales; el acento, los detalles y el botón de ingreso.
                            </div>
                            <v-row dense>
                                <v-col cols="12" sm="6" md="4" v-for="color in COLORES" :key="color.clave">
                                    <v-text-field v-model="modelo.colores[color.clave]" :label="color.etiqueta" outlined dense maxlength="7"
                                                  :rules="[v => /^#[0-9A-Fa-f]{6}$/.test(v || '') || 'Formato #RRGGBB']"
                                                  @blur="normalizarColor(color.clave)">
                                        <template v-slot:prepend-inner>
                                            <label class="selector-color" :style="{background: modelo.colores[color.clave]}" :title="`Elegir ${color.etiqueta.toLowerCase()}`">
                                                <input type="color" :value="colorNativo(modelo.colores[color.clave])" @input="modelo.colores[color.clave] = $event.target.value.toUpperCase()">
                                            </label>
                                        </template>
                                    </v-text-field>
                                </v-col>
                            </v-row>

                            <div class="text-subtitle-2 font-weight-bold mt-2 mb-1">Fuente</div>
                            <v-row dense>
                                <v-col cols="12" sm="7">
                                    <v-select v-model="modelo.fuente.familia" :items="fuentes" label="Familia tipográfica" outlined dense
                                              hint="Las fuentes de la lista se cargan de Google Fonts. «Sistema» usa la del equipo del visitante." persistent-hint>
                                        <template v-slot:item="{ item }">
                                            <span :style="{fontFamily: item === 'Sistema' ? 'system-ui, sans-serif' : `'${item}', sans-serif`}">{{ item }}</span>
                                        </template>
                                    </v-select>
                                </v-col>
                            </v-row>

                            <div class="text-subtitle-2 font-weight-bold mt-4 mb-1">Logos</div>
                            <div class="text-caption grey--text text--darken-1 mb-2">
                                PNG, SVG o WEBP de hasta 2 MB cada uno. Si no sube uno, se usa el logo actual del sistema.
                            </div>
                            <v-row dense>
                                <v-col cols="12" md="4" v-for="tipo in LOGOS" :key="tipo.clave">
                                    <div class="logo-actual" :class="{'logo-actual--vacio': !logosPrevia[tipo.clave]}">
                                        <img v-if="logosPrevia[tipo.clave]" :src="logosPrevia[tipo.clave]" :alt="tipo.etiqueta">
                                        <span v-else class="text-caption grey--text">Sin logo propio</span>
                                    </div>
                                    <v-file-input v-model="archivos[tipo.clave]" :label="tipo.etiqueta" outlined dense show-size truncate-length="22"
                                                  accept=".png,.svg,.webp,image/png,image/svg+xml,image/webp" prepend-icon="" prepend-inner-icon="image"
                                                  :rules="[reglaLogo]" :hint="tipo.ayuda" persistent-hint/>
                                    <v-btn v-if="tieneLogoGuardado(tipo.clave) && !archivos[tipo.clave]" x-small text :color="quitar[tipo.clave] ? 'grey' : 'error'"
                                           @click="quitar[tipo.clave] = !quitar[tipo.clave]">
                                        <v-icon x-small left>{{ quitar[tipo.clave] ? 'undo' : 'delete' }}</v-icon>
                                        {{ quitar[tipo.clave] ? 'Conservar el logo' : 'Quitar el logo propio' }}
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-col>

                    <v-col cols="12" md="5">
                        <tema-vista-previa :colores="coloresPrevia" :fuente="modelo.fuente" :logos="logosPrevia"/>
                    </v-col>
                </v-row>
            </v-card-text>

            <v-card-actions class="px-6 pb-4">
                <v-spacer></v-spacer>
                <v-btn text @click="cerrar(false)">Cancelar</v-btn>
                <v-btn color="primary" depressed :loading="guardando" @click="guardar">
                    <v-icon small left>save</v-icon> Guardar
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
/**
 * Formulario de un tema (nuevo o edición) con vista previa en vivo.
 * Envía multipart a /apariencia/temas/guardar y emite "guardado" con los datos
 * actualizados de la pantalla.
 */
import TemaVistaPrevia from "@/components/Apariencia/TemaVistaPrevia.vue";
import {cargarFuenteGoogle, COLORES_FABRICA, FUENTES, urlAbsoluta} from "@/services/apariencia";

const MAXIMO_BYTES = 2 * 1024 * 1024;
const TIPOS_LOGO = ['gobierno', 'infop', 'favicon'];

export default {
    name: "TemaFormulario",
    components: {TemaVistaPrevia},
    props: {
        value: {type: Boolean, default: false},
        tema: {type: Object, default: null},
        fuentes: {type: Array, default: () => FUENTES},
    },
    data() {
        return {
            COLORES: [
                {clave: 'primario', etiqueta: 'Primario'},
                {clave: 'secundario', etiqueta: 'Secundario'},
                {clave: 'acento', etiqueta: 'Acento'},
                {clave: 'fondo', etiqueta: 'Fondo'},
                {clave: 'texto', etiqueta: 'Texto'},
            ],
            LOGOS: [
                {clave: 'gobierno', etiqueta: 'Logo del Gobierno', ayuda: 'Menú lateral y tarjeta de acceso.'},
                {clave: 'infop', etiqueta: 'Logo INFOP', ayuda: 'Placa del panel de acceso.'},
                {clave: 'favicon', etiqueta: 'Favicon', ayuda: 'Icono de la pestaña (cuadrado).'},
            ],
            valido: true,
            guardando: false,
            modelo: this.vacio(),
            archivos: {gobierno: null, infop: null, favicon: null},
            quitar: {gobierno: false, infop: false, favicon: false},
            urlsTemporales: {gobierno: null, infop: null, favicon: null},
        };
    },
    computed: {
        coloresPrevia() {
            const colores = {};
            this.COLORES.forEach(c => {
                const v = this.modelo.colores[c.clave];
                colores[c.clave] = /^#[0-9A-Fa-f]{6}$/.test(v || '') ? v : COLORES_FABRICA[c.clave];
            });
            return colores;
        },
        logosPrevia() {
            const logos = {};
            TIPOS_LOGO.forEach(tipo => {
                if (this.urlsTemporales[tipo]) {
                    logos[tipo] = this.urlsTemporales[tipo];
                } else if (this.quitar[tipo]) {
                    logos[tipo] = null;
                } else {
                    logos[tipo] = this.tema && this.tema.logos ? urlAbsoluta(this.tema.logos[tipo]) : null;
                }
            });
            return logos;
        },
    },
    watch: {
        value(abierto) {
            if (abierto) this.iniciar();
            else this.liberarUrls();
        },
        'modelo.fuente.familia'(familia) {
            cargarFuenteGoogle(familia, 'apariencia-fuente-previa');
        },
        archivos: {
            deep: true,
            handler(archivos) {
                TIPOS_LOGO.forEach(tipo => {
                    if (this.urlsTemporales[tipo]) URL.revokeObjectURL(this.urlsTemporales[tipo]);
                    const archivo = archivos[tipo];
                    this.urlsTemporales[tipo] = archivo && archivo.size <= MAXIMO_BYTES ? URL.createObjectURL(archivo) : null;
                    if (archivo) this.quitar[tipo] = false;
                });
            },
        },
    },
    methods: {
        vacio() {
            return {
                tema_id: null,
                nombre: '',
                descripcion: '',
                activo: true,
                colores: {...COLORES_FABRICA},
                fuente: {familia: 'Roboto', google: true},
            };
        },
        iniciar() {
            const base = this.vacio();
            if (this.tema) {
                this.modelo = {
                    ...base,
                    tema_id: this.tema.tema_id,
                    nombre: this.tema.nombre || '',
                    descripcion: this.tema.descripcion || '',
                    activo: this.tema.activo !== false,
                    colores: {...base.colores, ...(this.tema.colores || {})},
                    fuente: {...base.fuente, ...(this.tema.fuente || {})},
                };
            } else {
                this.modelo = base;
            }
            this.archivos = {gobierno: null, infop: null, favicon: null};
            this.quitar = {gobierno: false, infop: false, favicon: false};
            this.liberarUrls();
            cargarFuenteGoogle(this.modelo.fuente.familia, 'apariencia-fuente-previa');
            this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
        },
        liberarUrls() {
            TIPOS_LOGO.forEach(tipo => {
                if (this.urlsTemporales[tipo]) URL.revokeObjectURL(this.urlsTemporales[tipo]);
                this.urlsTemporales[tipo] = null;
            });
        },
        tieneLogoGuardado(tipo) {
            return !!(this.tema && this.tema.logos && this.tema.logos[tipo]);
        },
        colorNativo(valor) {
            return /^#[0-9A-Fa-f]{6}$/.test(valor || '') ? valor.toLowerCase() : '#000000';
        },
        normalizarColor(clave) {
            const v = String(this.modelo.colores[clave] || '').trim();
            const conAlmohadilla = v && !v.startsWith('#') ? `#${v}` : v;
            const corto = /^#[0-9A-Fa-f]{3}$/.test(conAlmohadilla) ? '#' + conAlmohadilla.slice(1).split('').map(c => c + c).join('') : conAlmohadilla;
            this.modelo.colores[clave] = corto.toUpperCase();
        },
        reglaLogo(archivo) {
            if (!archivo) return true;
            if (archivo.size > MAXIMO_BYTES) return 'El archivo supera 2 MB';
            if (!/\.(png|svg|webp)$/i.test(archivo.name)) return 'Solo PNG, SVG o WEBP';
            return true;
        },
        cerrar(valor) {
            if (!this.guardando) this.$emit('input', !!valor);
        },
        guardar() {
            if (!this.$refs.form.validate()) return;

            this.guardando = true;
            const formData = new FormData();
            if (this.modelo.tema_id) formData.append('tema_id', this.modelo.tema_id);
            formData.append('nombre', this.modelo.nombre.trim());
            formData.append('descripcion', (this.modelo.descripcion || '').trim());
            formData.append('activo', this.modelo.activo ? 1 : 0);
            this.COLORES.forEach(c => formData.append(`colores[${c.clave}]`, this.modelo.colores[c.clave].toUpperCase()));
            formData.append('fuente[familia]', this.modelo.fuente.familia);
            formData.append('fuente[google]', this.modelo.fuente.familia === 'Sistema' ? 0 : 1);
            TIPOS_LOGO.forEach(tipo => {
                if (this.archivos[tipo]) formData.append(`logo_${tipo}`, this.archivos[tipo]);
                else if (this.quitar[tipo]) formData.append(`quitar_logo_${tipo}`, 1);
            });

            window.axios.post('/apariencia/temas/guardar', formData).then(response => {
                if (response.data.success) {
                    this.$toast.success(response.data.message);
                    this.$emit('guardado', response.data.data);
                    this.$emit('input', false);
                } else {
                    this.$toast.error(response.data.message, {timeout: 10000});
                }
            })
                .catch(error => {
                    const cuerpo = error.response && error.response.data;
                    const detalle = cuerpo && cuerpo.data && typeof cuerpo.data === 'object' ? [].concat(...Object.values(cuerpo.data))[0] : null;
                    this.$toast.error(detalle || (cuerpo && cuerpo.message) || 'No se pudo guardar el tema.', {timeout: 10000});
                })
                .finally(() => {
                    this.guardando = false;
                });
        },
    },
    beforeDestroy() {
        this.liberarUrls();
        cargarFuenteGoogle(null, 'apariencia-fuente-previa');
    },
}
</script>

<style scoped lang="scss">
.selector-color {
    display: inline-block;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    border: 1px solid rgba(0, 0, 0, .18);
    margin-right: 6px;
    margin-top: 1px;
    cursor: pointer;
    position: relative;
    overflow: hidden;

    input {
        position: absolute;
        inset: 0;
        opacity: 0;
        width: 100%;
        height: 100%;
        cursor: pointer;
    }
}

.logo-actual {
    height: 64px;
    border: 1px dashed #DCE1EC;
    border-radius: 8px;
    display: grid;
    place-items: center;
    margin-bottom: 8px;
    background: repeating-conic-gradient(#F3F5FA 0% 25%, #fff 0% 50%) 50% / 16px 16px;

    img {
        max-height: 56px;
        max-width: 90%;
        object-fit: contain;
    }

    &--vacio {
        background: #FAFBFD;
    }
}
</style>
